import {_} from 'lodash';
import * as ko from "knockout";
import {ObjectTypeId} from "../../constants";
import {IMessageData, ISyncField, TSyncfield} from "../../index";
import {getStoreDefaultValue, reInitStoreId, storeObservable, TStoreId} from "../../util/sessionstore";
import {lm} from "../../appContext";
import {IRequestObject} from "../../req/index";
import {nsSyncFields} from "../../req/nsSyncFields";
import {getObjectDefinition, getSourceTranslation, IObjectDefinition} from "../../util/misc";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {IComboboxV2Options} from "../combobox/v2";
import {nsTooltip} from "../../util/nsTooltip";
import syncfieldsHtml from './syncfields.html?raw';

export let template: string = syncfieldsHtml;

interface IParams {
    objecttypeid: ObjectTypeId
}

export class viewModel {
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly objDef: IObjectDefinition
    private readonly objectTypeId: ObjectTypeId;
    private reqGetSyncFields!: IRequestObject;

    private allSyncFields: KnockoutObservable<ISyncField[] | null> = ko.observable(null);
    private readonly _selectedSyncfields: KnockoutObservableArray<TSyncfield>
    private readonly selectedSyncfields: KnockoutObservableArray<TSyncfield> = ko.observableArray()
    private readonly availableSyncFields = ko.pureComputed(() => {
        return _(this.allSyncFields())
            .filter((sf: ISyncField) => !this.selectedSyncfields().includes(sf.name))
            .sortBy((sf: ISyncField) => this.getSourceTranslation(sf))
            .value();
    });
    private storeId: TStoreId;
    private readonly syncfieldPickerInput = ko.observable('')
    private v2Options_field!: IComboboxV2Options<ISyncField>

    constructor(private params: IParams) {
        this.objectTypeId = params.objecttypeid;
        this.objDef = getObjectDefinition(this.objectTypeId)!;
        this.storeId = `search.object.syncfields${params.objecttypeid}`;
        this._selectedSyncfields = storeObservable(this.storeId) as KnockoutObservableArray<TSyncfield>;
        storeObservable('language').subscribe(() => this.init())
        this.init();
    }

    protected init() {
        this.isReady(false);
        this.selectedSyncfields(_.cloneDeep(this._selectedSyncfields()))
        this.messageData({message: REQUEST_MESSAGE});
        this.reqGetSyncFields = nsSyncFields.getSyncFields(this.params.objecttypeid, storeObservable('language')().id);
        this.reqGetSyncFields.request
            .then((data: ISyncField[]) => {
                this.allSyncFields(data)
                this.v2Options_field = {
                    inputAttrs: {placeholder: lm.t('nav-right.section.searchcolumns.addfield|td')},
                    data: this.availableSyncFields,
                    enable: () => !!this.availableSyncFields().length,
                    input: this.syncfieldPickerInput,
                    selectionMode: 'pick',
                    option: {
                        label: o => this.getSourceTranslation(o),
                        tooltip: o => o ? lm.t(nsTooltip.getTitle_syncfield(o) ?? '') : '',
                    },
                    onSelect: (o: ISyncField | null) => {
                        if (o) {
                            this.selectedSyncfields.push(o.name);
                        }
                    },
                    inputStatus: () => 'ok',
                }
                this.messageData(null);
                this.isReady(true);
            });
    }

    private getSourceTranslation(syncfield: string | ISyncField): string {
        const sf = typeof syncfield == 'string' ? this.getSyncFieldFromName(syncfield) : syncfield;
        return sf ? getSourceTranslation(sf) : syncfield as string;
    }

    private getSyncFieldFromName(name: string): ISyncField | undefined {
        return this.allSyncFields()?.find(sf => sf.name == name);
    }

    private onMoveUpClick(key: TSyncfield) {
        const index = this.selectedSyncfields().findIndex(k => k == key);
        const isFirst = index == 0;
        this.selectedSyncfields.splice(index, 1);
        if (isFirst) {
            this.selectedSyncfields.push(key);
        } else {
            this.selectedSyncfields.splice(index - 1, 0, key);
        }
        const el = document.getElementById(this.getUpId(key));
        setTimeout(() => el?.focus());
    }

    private onMoveDownClick(key: TSyncfield) {
        const index = this.selectedSyncfields().findIndex(k => k == key);
        const isLast = index == this.selectedSyncfields().length - 1;
        this.selectedSyncfields.splice(index, 1);
        if (isLast) {
            this.selectedSyncfields.unshift(key);
        } else {
            this.selectedSyncfields.splice(index + 1, 0, key);
        }
        const el = document.getElementById(this.getDownId(key));
        setTimeout(() => el?.focus());
    }

    private onDeleteClick(key: string) {
        const index = this.selectedSyncfields().findIndex(k => k == key);
        this.selectedSyncfields.splice(index, 1);
    }

    private getUpId(key: string): string {
        return `btn-sf-up-${this.params.objecttypeid}-${key}`;
    }

    private getDownId(key: string): string {
        return `btn-sf-down-${this.params.objecttypeid}-${key}`;
    }

    private onSubmitClick() {
        this._selectedSyncfields(_.cloneDeep(this.selectedSyncfields()))
    }

    private onRestoreClick() {
        this.selectedSyncfields(_.cloneDeep(getStoreDefaultValue(this.storeId)));
    }

    private isChanged = ko.pureComputed(() => {
        return !_.isEqual(this.selectedSyncfields(), this._selectedSyncfields());
    })

    private isRestorable = ko.pureComputed(() => {
        return !_.isEqual(this.selectedSyncfields(), getStoreDefaultValue(this.storeId));
    })

    dispose() {
        this.reqGetSyncFields.promise.abort();
    }

}
